import { action, computed, IObservableArray, observable } from "mobx";
import * as DocumentManagement from "../DocumentManagement";
import * as Share from "../Shared";
import { DialogStore } from "../Stores/DialogStore";
import { DocumentMetaDataWizardStore } from "../Stores/DocumentMetaDataWizardStore";
import { DocumentPrivilegesWizardStore } from "../Stores/DocumentPrivilegesWizardStore";
import { Strings } from "../Stores/StringsStore";
import { AppStore, AreaStore } from "./AppStore";
import { FileLanguageWizardStore } from "./FileLanguageWizardStore";
import { HierarchyMetaValuesWizardStore } from "./HierarchyMetaValuesWizardStore";
import { DocumentMetaDataSelectDocumentsWizardStore } from "./DocumentMetaDataSelectDocumentsWizardStore";


export class DocumentStoreImpl extends AreaStore {

	/**
	* duplicate array for storing privileges  for document
	*/
	tempStorageDocumentPrivileges: DocumentManagement.DocumentPrivileges = new DocumentManagement.DocumentPrivileges();
	/**
	* Holds the wizard context for adding privileges to a selected document.
	*/
	privilegesWizardStore: DocumentPrivilegesWizardStore = new DocumentPrivilegesWizardStore(this);

	/**
	* Holds the wizard context for adding meta data to a selected document.
	*/
	metaDataWizardStore: DocumentMetaDataWizardStore = new DocumentMetaDataWizardStore(this);

	hierarchyMetaValueWizardStore: HierarchyMetaValuesWizardStore = new HierarchyMetaValuesWizardStore(this);

	metaDataSelectDocumentsWizardStore: DocumentMetaDataSelectDocumentsWizardStore = new DocumentMetaDataSelectDocumentsWizardStore(this);

	fileLanguageWizardStore: FileLanguageWizardStore = new FileLanguageWizardStore(this);

	/**
	* Current DocumentDetails model object
	*/
	@observable model = new DocumentManagement.DocumentDetails();

	/**
	* Holds all current selected document metas
	*/
	addedDocumentMetas: IObservableArray<string> = observable.array<string>();

	@observable selectedHierarchyMetaValue: string;

	existingHierarchyMetaValue: DocumentManagement.DocumentMetaValue;

	/**
	* Sets the metas section Document-Edit component to the selection mode.
	*/
	@observable metaSelectionMode: boolean = false;

	selectedDocumentMetas: IObservableArray<DocumentManagement.DocumentMeta> = observable.array<DocumentManagement.DocumentMeta>();

	/**
	* Sets the files section Document-Edit component to the selection mode.
	*/
	@observable fileSelectionMode: boolean = false;

	selectedFiles: IObservableArray<DocumentManagement.FileDetails> = observable.array<DocumentManagement.FileDetails>();

	@observable selectedFileLanguage: string;

	/**
	* Sets the privileges section Document-Edit component to the selection mode.
	*/
	@observable privilegeSelectionMode: boolean = false;
	/**
	* Current added privileges
	*/
	@observable addedPrivileges: DocumentManagement.PrivilegesForGroup[] = [];

	@observable addedFiles: DocumentManagement.FileDetails[] = [];
	/**
	* Holds deleted privileges for document
	*/
	removedDocumentPrivileges: DocumentManagement.PrivilegesForGroup[] = [];
	/**
	* All current selected privilege items
	*/
	selectedPrivileges: IObservableArray<DocumentManagement.PrivilegesForGroup> = observable<DocumentManagement.PrivilegesForGroup>([]);
	/**
	* Callback for saving selected Privileges to the CollectionStore
	*/
	public saveSelectedPrivilege: () => void
	/**
	* Set the component to the add privileges mode.
	*/
	@observable addPrivilegesMode: boolean = false;

	/**
	* Set the component to the add meta mode.
	*/
	@observable addMetaMode: boolean = false;

	selectedDocuments: IObservableArray<string> = observable<string>([]);

	addedDocuments: IObservableArray<string> = observable<string>([]);

	existingDocumentLinkMetaValue: DocumentManagement.DocumentMetaValue;

	@observable selectHierarchyMetaValueMode: boolean = false;

	isDocumentLinkMultiple: boolean = false;

	/**
	 * Maps DocumentManagement.DocumentMeta[] to DocumentManagement.DocumentMetaUpdate[]
	 * @param documentMeta The current document DocumentManagement.DocumentMeta[]
	 */
	get getDocumentMetaUpdate(): DocumentManagement.DocumentMetaUpdate[] {
		let documentMetaUpdate: DocumentManagement.DocumentMetaUpdate[] = [];
		this.model.metas.forEach((metaData: DocumentManagement.DocumentMeta) => {
			metaData.values.forEach((metaValue) => {
				documentMetaUpdate.push(this.resolveMetaValue(metaData, metaValue));
			});
		});
		return documentMetaUpdate;
	}

	/**
	 * Maps an DocumentMeta object to an DocumentMetaUpdate object
	 * @param documentMeta Current DocumentManagement.DocumentMeta object
	 * @param documentMetaValue DocumentManagement.DocumentMetaValue object
	 */
	resolveMetaValue(documentMeta: DocumentManagement.DocumentMeta, documentMetaValue: DocumentManagement.DocumentMetaValue): DocumentManagement.DocumentMetaUpdate {
		let documentMetaUpdate = new DocumentManagement.DocumentMetaUpdate();
		documentMetaUpdate.guid = documentMetaValue.guid;
		documentMetaUpdate.metaId = documentMeta.metaGuid;
		documentMetaUpdate.lastModified = documentMetaValue.lastModified;
		switch (documentMeta.inputType) {
			case Share.InputType.InputChoice:
				documentMetaUpdate.value = documentMetaValue.valueChoice.value;
				break;
			case Share.InputType.InputDate:
				const date = new Date(documentMetaValue.valueDate.value.toString());
				documentMetaUpdate.value = date.toISOString();
				break;
			case Share.InputType.InputNumber:
				documentMetaUpdate.value = documentMetaValue.valueNumber.value.toString();
				break;
			case Share.InputType.InputText:
				documentMetaUpdate.value = documentMetaValue.valueText.value;
				documentMetaUpdate.valueNeedsTranslation = documentMetaValue.valueText.valueNeedsTranslation;
				if (documentMetaUpdate.valueNeedsTranslation) {
					documentMetaUpdate.untranslatedValue = documentMetaValue.valueText.placeHolder;
				}
				break;
			case Share.InputType.InputHierarchy:
				documentMetaUpdate.value = documentMetaValue.valueHierarchy.value;
				break;
			case Share.InputType.InputDocumentLink:
				documentMetaUpdate.value = documentMetaValue.valueDocumentLink.value;
				break;
		}
		return documentMetaUpdate;
	}

	@computed get hasSelectedPrivilegeItems(): boolean {
		return this.selectedPrivileges && this.selectedPrivileges.length > 0;
	}

	@computed get hasSelectedFileItems(): boolean {
		return this.selectedFiles && this.selectedFiles.length > 0;
	}

	@computed get hasSelectedMetaItems(): boolean {
		return this.selectedDocumentMetas && this.selectedDocumentMetas.length > 0;
	}

	@action public togglePrivilegeSelectionMode = (): void => {
		this.privilegeSelectionMode = !this.privilegeSelectionMode;
		if (!this.privilegeSelectionMode) {
			this.selectedPrivileges.clear();
		}
	}

	@action public toggleFileSelectionMode = (): void => {
		this.fileSelectionMode = !this.fileSelectionMode;
		if (!this.fileSelectionMode) {
			this.selectedFiles.clear();
		}
	}

	@action public toggleMetaSelectionMode = (): void => {
		this.metaSelectionMode = !this.metaSelectionMode;
		if (!this.metaSelectionMode) {
			this.selectedDocumentMetas.clear();
		}
	}

	public handlePrivilegesDelete: () => void;
	public handleMetasDelete: () => void;
	public handleFilesDelete: () => void;

	public updateImplicitPrivileges(document: DocumentManagement.DocumentDetails, collectionId: string) {
		let privileges: DocumentManagement.DocumentPrivileges = new DocumentManagement.DocumentPrivileges();
		privileges.documentId = document.guid;
		document.privileges.privilegesForGroups.forEach((privilege) => {
			privileges.privilegesForGroups.push(privilege);
		});
		this.addedPrivileges.forEach((privilege) => {
			privileges.privilegesForGroups.push(privilege);
		});
		DocumentManagement.getImplicitDocumentPrivilegesAsync({ collectionId: collectionId, privileges: privileges }).then(result => {
			this.updatePrivileges(document.privileges.privilegesForGroups, result.privilegesForGroups);
			this.updatePrivileges(this.addedPrivileges, result.privilegesForGroups);
			result.privilegesForGroups.forEach(resultPrivilege => {
				if (!document.privileges.privilegesForGroups.some(cp => cp.groupId === resultPrivilege.groupId) && !this.addedPrivileges.some(ap => ap.groupId === resultPrivilege.groupId)) {
					if (this.removedDocumentPrivileges.some(p => p.groupId === resultPrivilege.groupId)) {
						this.model.privileges.privilegesForGroups.push(resultPrivilege);
						this.removedDocumentPrivileges = this.removedDocumentPrivileges.filter(rp => rp.groupId !== resultPrivilege.groupId);
					}
					else {
						this.addedPrivileges.push(resultPrivilege);

					}
				}
			});
		}).catch(error => {
			AppStore.isBusy = false;
			DialogStore.showError(Strings.general.errorDialogTitle, error);
		});
	}
	@action updatePrivileges(privilegesForGroup: DocumentManagement.PrivilegesForGroup[], result: DocumentManagement.PrivilegesForGroup[]) {
		privilegesForGroup.forEach(p => {
			let resultPrivileges = result.filter(r => r.groupId === p.groupId);
			if (resultPrivileges.length === 0) {
				p.privilegeMemberships.forEach(m => {
					m.isExplicitMemberOf = false;
					m.isImplicitMemberOf = false;
				});
			}
			else {
				p.privilegeMemberships = resultPrivileges[0].privilegeMemberships;
			}
		});
	}
}